import { Sim } from '../../game/simulation';
import type { Diorama } from '../../game/three/Diorama';

/**
 * Work-progress bars.
 *
 * Two modes:
 *   • Overview / free-tile mode (no plot active): per-tile floating bars
 *     above any world tile currently being hoed / watered / seeded. They
 *     fade after activity stops.
 *   • Plot mode (a plot is active): a single set of three big aggregate
 *     bars at top-center showing the whole plot's till / water / seed
 *     progress, sourced from Sim.plotTill/Water/SeedProgress. The per-
 *     tile bars are suppressed.
 *
 * plotAutoCompleted bursts the matching aggregate bar to 100%.
 */
type Mode = 'till' | 'water' | 'seed';

interface BarEntry {
  c: number;
  r: number;
  mode: Mode;
  el: HTMLDivElement;
  fillEl: HTMLDivElement;
  labelEl: HTMLSpanElement;
  lastFrac: number;
  lastTouchMs: number;
  forcedFull: boolean;
}

const IDLE_MS = 1200;
const FADE_MS = 600;
const STALE_MS = 10_000;

export class TileProgressBars {
  private root: HTMLDivElement;
  private bars = new Map<string, BarEntry>();

  constructor(parent: HTMLElement, private sim: Sim, private diorama: Diorama) {
    this.root = document.createElement('div');
    this.root.className = 'ui-tilebars';
    parent.appendChild(this.root);

    sim.bus.on(e => {
      switch (e.type) {
        case 'subCellTilled':
          this.touch(e.col, e.row, 'till');
          break;
        case 'subCellWatered':
          this.touch(e.col, e.row, 'water');
          break;
        case 'subCellSeeded':
          this.touch(e.col, e.row, 'seed');
          break;
        case 'tileAutoCompleted':
          this.flashFull(e.col, e.row, e.mode);
          break;
        case 'plotEntered':
        case 'plotExited':
          this.pruneForMode();
          break;
      }
    });
  }

  private touch(c: number, r: number, mode: Mode) {
    if (!this.canShow(c, r)) return;
    const key = `${mode}:${c}:${r}`;
    let bar = this.bars.get(key);
    if (!bar) {
      bar = this.create(c, r, mode);
      this.bars.set(key, bar);
    }
    bar.lastTouchMs = performance.now();
  }

  private flashFull(c: number, r: number, mode: Mode) {
    if (!this.canShow(c, r)) return;
    const key = `${mode}:${c}:${r}`;
    const bar = this.bars.get(key) ?? this.create(c, r, mode);
    this.bars.set(key, bar);
    bar.forcedFull = true;
    bar.lastTouchMs = performance.now();
    bar.el.classList.add('ui-tilebar--burst');
  }

  /** Per-tile bars only render OUTSIDE plot mode (world tiles only).
   *  In plot mode the player sees the three aggregate bars instead
   *  (rendered by PlotPanel.ts). */
  private canShow(c: number, r: number): boolean {
    const inPlotMode = this.sim.state.activePlotIdx != null;
    if (inPlotMode) return false;
    return !this.isPlotTile(c, r);
  }

  private isPlotTile(c: number, r: number): boolean {
    for (const p of this.sim.state.plots) {
      for (const t of p.tiles) {
        if (t.c === c && t.r === r) return true;
      }
    }
    return false;
  }

  private pruneForMode() {
    for (const [key, bar] of this.bars) {
      if (!this.canShow(bar.c, bar.r)) {
        bar.el.remove();
        this.bars.delete(key);
      }
    }
  }

  private create(c: number, r: number, mode: Mode): BarEntry {
    const el = document.createElement('div');
    el.className = `ui-tilebar ui-tilebar--${mode}`;
    const fillEl = document.createElement('div');
    fillEl.className = 'ui-tilebar__fill';
    const labelEl = document.createElement('span');
    labelEl.className = 'ui-tilebar__label';
    labelEl.textContent = '0%';
    el.appendChild(fillEl);
    el.appendChild(labelEl);
    this.root.appendChild(el);
    return { c, r, mode, el, fillEl, labelEl, lastFrac: -1,
             lastTouchMs: performance.now(), forcedFull: false };
  }

  update(_dt: number) {
    this.updatePerTileBars();
  }

  private updatePerTileBars() {
    const now = performance.now();
    for (const [key, bar] of this.bars) {
      if (!this.canShow(bar.c, bar.r)) {
        bar.el.remove();
        this.bars.delete(key);
        continue;
      }
      let frac: number;
      if (bar.forcedFull) {
        frac = 1;
      } else if (bar.mode === 'till') {
        frac = this.sim.tileTillProgress(bar.c, bar.r).fraction;
      } else if (bar.mode === 'water') {
        frac = this.sim.tileWaterProgress(bar.c, bar.r).fraction;
      } else {
        frac = this.sim.tileSeedProgress(bar.c, bar.r).fraction;
      }
      if (Math.abs(frac - bar.lastFrac) >= 0.005) {
        const pct = Math.round(frac * 100);
        bar.fillEl.style.width = `${pct}%`;
        bar.labelEl.textContent = `${pct}%`;
        bar.lastFrac = frac;
      }
      const pos = this.diorama.tileScreen(bar.c, bar.r);
      if (!pos) {
        bar.el.style.display = 'none';
        continue;
      }
      bar.el.style.display = '';
      bar.el.style.left = `${pos.x}px`;
      bar.el.style.top  = `${pos.y - 56}px`;

      const idleMs = now - bar.lastTouchMs;
      const isComplete = frac >= 0.999;
      const isEmpty = frac <= 0.001;
      let opacity = 1;
      if ((isComplete || isEmpty) && idleMs > IDLE_MS) {
        opacity = Math.max(0, 1 - (idleMs - IDLE_MS) / FADE_MS);
      } else if (idleMs > STALE_MS) {
        opacity = Math.max(0, 1 - (idleMs - STALE_MS) / FADE_MS);
      }
      if (opacity <= 0) {
        bar.el.remove();
        this.bars.delete(key);
        continue;
      }
      bar.el.style.opacity = String(opacity);
    }
  }
}
